export const VEHICLE_STATUSES = ['AVAILABLE', 'RENTED', 'MAINTENANCE', 'RETIRED'];
export const VEHICLE_CLASSES = [
    'ECONOMY',
    'COMPACT',
    'SEDAN',
    'SUV',
    'VAN',
    'PICKUP',
    'LUXURY',
];
/**
 * How far a car has got towards being bookable — a different question from
 * whether it is free this weekend, which is `VehicleStatus`.
 */
export const LISTING_STATUSES = [
    'DRAFT',
    'IN_REVIEW',
    'PUBLISHED',
    'REJECTED',
    'UNLISTED',
];
/** The one listing state a customer may book from. */
export const BOOKABLE_LISTING_STATUS = 'PUBLISHED';
/**
 * Whether a car is let with one of the partner's drivers, and whether it can
 * be let without.
 *
 * The capability belongs to the partner — `Partner.providesDrivers` — and the
 * price belongs to the car, so this says only what the customer may choose.
 */
export const DRIVER_POLICIES = ['SELF_DRIVE_ONLY', 'DRIVER_OPTIONAL', 'DRIVER_ONLY'];
/** What the fleet page's driver filter is set to. */
export const DRIVER_FILTERS = ['ANY', 'WITH_DRIVER', 'SELF_DRIVE'];
/** The policies each filter choice admits. `ANY` does not filter at all. */
export const DRIVER_FILTER_POLICIES = {
    WITH_DRIVER: ['DRIVER_OPTIONAL', 'DRIVER_ONLY'],
    SELF_DRIVE: ['SELF_DRIVE_ONLY', 'DRIVER_OPTIONAL'],
};
export const TRANSMISSIONS = ['MANUAL', 'AUTOMATIC'];
export const FUEL_TYPES = ['PETROL', 'DIESEL', 'HYBRID', 'ELECTRIC'];
/**
 * What a photograph actually shows.
 *
 * Rental listings converge on the same set of shots — the two opposite
 * three-quarter views that between them cover all four sides, the driver's
 * view of the dashboard, the cabin, the boot somebody has to fit suitcases
 * into. Naming them lets the gallery group pictures rather than dump them in
 * upload order, and lets the listing gate ask for the ones that actually
 * sell a car instead of counting to one.
 */
export const VEHICLE_IMAGE_KINDS = [
    // Exterior
    'FRONT_THREE_QUARTER',
    'REAR_THREE_QUARTER',
    'SIDE',
    'FRONT',
    'REAR',
    // Interior
    'DASHBOARD',
    'SEATS',
    'BOOT',
    // Detail and condition
    'FEATURE',
    'ODOMETER',
    'DAMAGE',
    'OTHER',
];
/** How the gallery lays the kinds out, and the order the slots appear in. */
export const VEHICLE_IMAGE_GROUPS = {
    EXTERIOR: ['FRONT_THREE_QUARTER', 'REAR_THREE_QUARTER', 'SIDE', 'FRONT', 'REAR'],
    INTERIOR: ['DASHBOARD', 'SEATS', 'BOOT'],
    DETAIL: ['FEATURE', 'ODOMETER', 'DAMAGE', 'OTHER'],
};
/** Which group a kind belongs to, for grouping a gallery in one pass. */
export const VEHICLE_IMAGE_GROUP_OF = Object.fromEntries(Object.entries(VEHICLE_IMAGE_GROUPS).flatMap(([group, kinds]) => kinds.map((kind) => [kind, group])));
/**
 * The shots a listing cannot go live without: what the car looks like from
 * both ends, and what the driver sees. Between them they cover all four
 * sides and the cabin, which is the least a customer needs to choose. The
 * cabin, boot and detail shots are worth having and worth asking for, and
 * not worth blocking a listing over.
 *
 * Read by the publishing gate in the API and by the partner's upload slots,
 * so the two can never ask for different things. The sentence in
 * `errors.listing.missingPhotoKinds` names them, so it changes with this.
 */
export const REQUIRED_VEHICLE_IMAGE_KINDS = [
    'FRONT_THREE_QUARTER',
    'REAR_THREE_QUARTER',
    'DASHBOARD',
];
/**
 * Where a photo sits in the gallery when nobody has said otherwise: the
 * order of `VEHICLE_IMAGE_KINDS`, which runs hero shot first and condition
 * photos last. Keeps a boot shot out of the cover slot just because it
 * happened to be uploaded first.
 *
 * Kinds are spaced so several photos of one can sit between two kinds —
 * `nth` is how many of that kind are already there.
 */
export function defaultImagePosition(kind, nth = 0) {
    return VEHICLE_IMAGE_KINDS.indexOf(kind) * 10 + Math.min(nth, 9);
}
/**
 * The merchandising tags a listing can wear on its photograph.
 *
 * A runtime array rather than a bare union, unlike `FleetShelfKey`: the
 * precedence table in `@zuruflex/core`, the tone map in `@zuruflex/brand` and the
 * spec that asserts every key is ranked exactly once all have to enumerate
 * these, and a type cannot be iterated.
 *
 * Every one of them is *derived*. There is deliberately no hand-set or
 * sponsored badge: the moment one exists the whole set reads as advertising
 * and none of the earned ones mean anything.
 */
export const VEHICLE_BADGE_KEYS = [
    // Earned — produced by other people's behaviour, and not typeable into a form.
    'guestFavourite',
    'rareFind',
    'topOwner',
    'newListing',
    'greatValue',
    // Given — a plain fact about the car, read off a column its owner filled in.
    'modelYear',
    'electric',
    'lowMileage',
    'noDeposit',
    'unlimitedKm',
];
export const EMPTY_FLEET_FILTERS = {
    q: '',
    classes: [],
    transmission: null,
    fuelType: null,
    minSeats: null,
    country: null,
    maxDailyRate: null,
    driver: 'ANY',
    badge: null,
    from: null,
    to: null,
    sort: 'relevance',
    page: 1,
};
